import React, { useState, useEffect, useCallback } from "react";
import axios from '../../../api/apiClient';

import {
  CreateTableNew as CreateTable,
  SearchBar,
  Button,
  GeneralMessage,
  useGeneralMessage
} from '../../../common/APICommon';
import { SetAuditTrail, SetAuditTrailBulk } from '../../Reports/AuditTrailReportEmployeeSetup';
import {
  PDFModal as PDFViewModal,
  ConfirmationModal
} from '../../../common/Modal';
import { PerformanceAppraisalSetupForm } from "./Setup/PerformanceAppraisalSetupForm";
import { PerformanceAppraisalForm } from "./Process/PerformanceAppraisalForm"; 
import { PerformanceAppraisalViewModal } from "./View/PerformanceAppraisalViewModal"; // NEW COMPONENT
import { token, domain } from '../../Security';
import { LoadingSpinner } from '../../../common/Loading';

// Icons
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteForever';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AssessmentIcon from '@mui/icons-material/Assessment'; 
import { StatusSelect } from "./Setup/formComponents/StatusSelect";

function KPIPerformanceAppraisal({ user, isHideSideMenu }) {
  const { showMessage, message, isSuccess, showGeneralMessage } = useGeneralMessage();

  const headers = ["ID", "Employee", "Appraiser", "Period", "Status", "Actions"];
  const accessors = ["id", "employeeName", "appraiserName", "period", "status", "actions"];

  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingRequest, setLoadingRequest] = useState(false);
  const [data, setData] = useState([]);
  const [filterData, setFilterData] = useState([]);
  const [selectedData, setSelectedData] = useState(null);

  // Status update states
  const [showStatusConfirmation, setShowStatusConfirmation] = useState(false);
  const [statusUpdateData, setStatusUpdateData] = useState({ id: null, newStatus: "", rowData: null });
  const [loadingStatusUpdate, setLoadingStatusUpdate] = useState(false);

  // Modal Visibility States
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showDeleteForm, setShowDeleteForm] = useState(false);
  const [showAppraisalModal, setShowAppraisalModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false); // NEW STATE

  // Form state for Setup/Edit
  const [form, setForm] = useState({
    employeePersonalInformationId: "",
    employeeData: null,
    raterEmployeePersonalInformationId: "",
    raterData: null,
    dateStart: "",
    dateEnd: "",
    timeStart: "",
    timeEnd: "",
    status: "Draft",
    performanceCategories: [],
    performanceRatingsLegendId: "",
    scores: []
  });

  const apiUrl = domain + "/api/KpiPerformanceAppraisal";

  const resetLocalVariables = () => {
    setForm({
      employeePersonalInformationId: "",
      employeeData: null,
      raterEmployeePersonalInformationId: "",
      raterData: null,
      dateStart: "",
      dateEnd: "",
      timeStart: "",
      timeEnd: "",
      status: "Draft",
      performanceCategories: [],
      performanceRatingsLegendId: 1,
      scores: []
    });
    setSelectedData(null);
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(apiUrl, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const formattedData = response.data.map(item => ({
        ...item,
        employeeName: item.employeeName || "N/A",
        appraiserName: item.appraiserName || "N/A",
        period: item.dateStart ? `${new Date(item.dateStart).toLocaleDateString()} - ${new Date(item.dateEnd).toLocaleDateString()}` : ""
      }));

      setData(formattedData);
      setFilterData(formattedData);
    } catch (error) {
      console.error('Error fetching data:', error);
      showGeneralMessage("Failed to fetch data from server", false);
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFilter = (value) => {
    setCurrentPage(1);
    const lowercaseValue = value.toLowerCase();
    if (!value) {
      setData(filterData);
      return;
    }
    const res = filterData.filter((f) => {
      return (
        f.employeeName?.toLowerCase().includes(lowercaseValue) ||
        f.appraiserName?.toLowerCase().includes(lowercaseValue) ||
        f.status?.toLowerCase().includes(lowercaseValue) ||
        f.id?.toString().includes(lowercaseValue)
      );
    });
    setData(res);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const apiData = {
      employeePersonalInformationId: parseInt(form.employeePersonalInformationId),
      raterEmployeePersonalInformationId: parseInt(form.raterEmployeePersonalInformationId),
      performanceRatingsLegendId: parseInt(form.performanceRatingsLegendId),
      dateStart: form.dateStart,
      dateEnd: form.dateEnd,
      timeStart: form.timeStart || "00:00",
      timeEnd: form.timeEnd || "00:00",
      status: form.status || "Draft",
      performanceCategories: form.performanceCategories.map(cat => ({
        description: cat.description,
        totalPercentage: parseFloat(cat.totalPercentage),
        objectives: cat.objectives.map(obj => ({
          code: obj.code,
          description: obj.description,
          percentage: parseFloat(obj.percentage)
        }))
      }))
    };

    try {
      setLoadingRequest(true);
      await axios.post(apiUrl, apiData, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });

      await SetAuditTrail({
        setupArea: "KPI Performance Appraisal",
        actionTaken: "insert",
        field: "Add",
        newValue: `Created Appraisal for Emp ID: ${apiData.employeePersonalInformationId}`,
        oldValue: "-",
        dateSaved: new Date(),
        savedById: user.id
      });

      showGeneralMessage("KPI Performance Appraisal created successfully!", true);
      closeAddHandleClick();
      fetchData();
    } catch (error) {
      showGeneralMessage(error.response?.data?.message || "Failed to create appraisal", false);
    } finally {
      setLoadingRequest(false);
    }
  };

  const handleUpdate = async (event) => {
    event.preventDefault();

    const cleanId = (id) => (id > 2000000000 ? 0 : id);

    const apiData = {
      id: selectedData.id,
      employeePersonalInformationId: parseInt(form.employeePersonalInformationId),
      raterEmployeePersonalInformationId: parseInt(form.raterEmployeePersonalInformationId),
      performanceRatingsLegendId: parseInt(form.performanceRatingsLegendId),
      dateStart: form.dateStart,
      dateEnd: form.dateEnd,
      timeStart: form.timeStart,
      timeEnd: form.timeEnd,
      status: form.status,
      performanceCategories: form.performanceCategories.map(cat => ({
        id: cleanId(cat.id),
        description: cat.description,
        totalPercentage: parseFloat(cat.totalPercentage),
        objectives: (cat.objectives || []).map(obj => ({
          id: cleanId(obj.id),
          code: obj.code,
          description: obj.description,
          percentage: parseFloat(obj.percentage)
        }))
      }))
    };

    try {
      setLoadingRequest(true);
      await axios.put(`${apiUrl}/${selectedData.id}`, apiData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      fetchData();
      closeEditHandleClick();
      showGeneralMessage("KPI Performance Appraisal updated successfully!", true);
    } catch (error) {
      showGeneralMessage("Update failed", false);
    } finally {
      setLoadingRequest(false);
    }
  };

  const confirmStatusUpdate = async () => {
    const { id, newStatus, rowData } = statusUpdateData;
    try {
      setLoadingStatusUpdate(true);
      await axios.put(`${apiUrl}/update-status/${id}`, { status: newStatus }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchData();
      showGeneralMessage(`Status updated to ${newStatus}`, true);
    } catch (error) {
      showGeneralMessage("Failed to update status", false);
    } finally {
      setShowStatusConfirmation(false);
      setLoadingStatusUpdate(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoadingRequest(true);
      await axios.delete(`${apiUrl}/${selectedData.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      showGeneralMessage("KPI Performance Appraisal deleted successfully!", true);
      closeDeleteHandleClick();
      fetchData();
    } catch (error) {
      showGeneralMessage("Failed to delete appraisal", false);
    } finally {
      setLoadingRequest(false);
    }
  };

  const editHandleClick = (data) => {
    setSelectedData(data);
    const extractTime = (isoString) => {
      if (!isoString) return "";
      if (isoString.includes('T')) {
        return isoString.split('T')[1].substring(0, 5);
      }
      return isoString;
    };

    setForm({
      employeePersonalInformationId: data.employeePersonalInformationId,
      employeeData: { fullName: data.employeeName, employeeId: data.employeeId, department: data.department, position: data.position },
      raterEmployeePersonalInformationId: data.raterEmployeePersonalInformationId,
      raterData: { fullName: data.appraiserName, position: data.raterPosition },
      dateStart: data.dateStart ? data.dateStart.split('T')[0] : "",
      dateEnd: data.dateEnd ? data.dateEnd.split('T')[0] : "",
      timeStart: extractTime(data.timeStart),
      timeEnd: extractTime(data.timeEnd),
      status: data.status,
      performanceCategories: data.performanceCategories || [],
      performanceRatingsLegendId: data.performanceRatingsLegendId || 1,
      scores: []
    });
    setShowEditForm(true);
  };

  const closeAddHandleClick = () => { setShowAddForm(false); resetLocalVariables(); };
  const closeEditHandleClick = () => { setShowEditForm(false); resetLocalVariables(); };
  const closeDeleteHandleClick = () => { setShowDeleteForm(false); setSelectedData(null); };

  const customCellRenderer = (row, accessor) => {
    if (accessor === "status") {
      return (
        <StatusSelect
          value={row.status}
          showLabel={false}
          onChange={(e) => {
            setStatusUpdateData({ id: row.id, newStatus: e.target.value, rowData: row });
            setShowStatusConfirmation(true);
          }}
        />
      );
    }
    return <span className="text-gray-700 px-2">{row[accessor] ?? "-"}</span>;
  };

  const actions = [
    { 
      name: "Appraise", 
      tooltip: "Evaluate Employee", 
      icon: <AssessmentIcon fontSize="small" />, 
      className: "bg-orange-500 hover:bg-orange-600", 
      function: (d) => { setSelectedData(d); setShowAppraisalModal(true); }, 
      visible: true 
    },
    { 
      name: "View", 
      icon: <VisibilityIcon fontSize="small" />, 
      className: "bg-sky-600 hover:bg-sky-700", 
      function: (d) => { setSelectedData(d); setShowViewModal(true); }, // UPDATED ACTION
      visible: true 
    },
    { 
      name: "Edit", 
      icon: <EditIcon fontSize="small" />, 
      className: "bg-green-500 hover:bg-green-600", 
      function: editHandleClick, 
      visible: true 
    },
    { 
      name: "Delete", 
      icon: <DeleteIcon fontSize="small" />, 
      className: "bg-red-500 hover:bg-red-600", 
      function: (d) => { setSelectedData(d); setShowDeleteForm(true); }, 
      visible: true 
    },
  ];

  return (
    <div>
      <div className={`MainBody row ${isHideSideMenu ? "MainBody2" : ""}`}>
        <div className="LeftSpace"></div>
        <div className={`BodySpace ${isHideSideMenu ? "BodySpace2" : ""}`}>
          <div className="MainteInfo shadow-xl">
            <div className="mainteHeader">
              <div className='sub-title row'>
                <span style={{ padding: "0 5px 0 0", color: "#b0b0b0" }}>{"Talent Management >"} </span>
                <span style={{ borderBottom: "1px solid black" }}>{"KPI Performance Appraisal"}</span>
              </div>
              <h3>KPI Performance Appraisal</h3>
            </div>
            <div className='space-y-4 p-4'>
              <SearchBar handleFilter={handleFilter} />
              <Button
                icon={<AddIcon />}
                name={"New Performance Appraisal"}
                className={"bg-customGreen hover:bg-customGreenHover border-none cursor-pointer"}
                onClick={() => setShowAddForm(true)}
              />
              <CreateTable
                tableName={"Performance Appraisals"}
                headers={headers}
                accessors={accessors}
                data={data}
                actions={actions}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                itemsPerPage={10}
                totalItems={data?.length}
                customCellRenderer={customCellRenderer}
              />
            </div>
          </div>
        </div>
      </div>

      {/* 1. Setup Form (Add/Edit) */}
      <PerformanceAppraisalSetupForm
        isOpen={showAddForm || showEditForm}
        onClose={showAddForm ? closeAddHandleClick : closeEditHandleClick}
        form={form}
        setForm={setForm}
        handleSubmit={showAddForm ? handleSubmit : handleUpdate}
        modalName={showAddForm ? "New Performance Appraisal" : "Edit Performance Appraisal"}
        mode={showAddForm ? "add" : "edit"}
        showGeneralMessage={showGeneralMessage}
        user={user}
        selectedData={selectedData}
      />

      {/* 2. Process Form (Evaluation/Scoring) */}
      <PerformanceAppraisalForm
        isOpen={showAppraisalModal}
        onClose={() => setShowAppraisalModal(false)}
        selectedData={selectedData}
        user={user}
        showGeneralMessage={showGeneralMessage}
        fetchMainData={fetchData} // Refresh table after evaluation
      />

      {/* 3. View Modal (Read Only Summary & Scores) */}
      <PerformanceAppraisalViewModal
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        data={selectedData}
      />

      {/* Confirmation Modals */}
      <ConfirmationModal
        visible={showDeleteForm}
        onClose={closeDeleteHandleClick}
        onConfirm={handleDelete}
        title="Delete Appraisal"
        message="Are you sure you want to delete this appraisal?"
        confirmText="Delete"
        type="danger"
        isLoading={loadingRequest}
      />

      <ConfirmationModal
        visible={showStatusConfirmation}
        onClose={() => setShowStatusConfirmation(false)}
        onConfirm={confirmStatusUpdate}
        title="Update Status"
        message={`Change status to ${statusUpdateData.newStatus}?`}
        isLoading={loadingStatusUpdate}
      />

      <GeneralMessage form={showMessage} message={message} isSuccess={isSuccess} />
      <LoadingSpinner loading={loading || loadingRequest || loadingStatusUpdate} />
    </div>
  );
}

export default KPIPerformanceAppraisal;